/**
 * Real-time audio analyser hook.
 *
 * Taps a live audio source — either a `MediaStream` (microphone recording) or an
 * `HTMLAudioElement` (playback) — via the Web Audio API and exposes an imperative
 * `readInto(out)` function that fills a caller-provided array with normalised 0–1
 * frequency magnitudes. Designed so visualisers can drive their existing
 * `requestAnimationFrame` DOM loop with REAL audio data instead of fake animation.
 *
 * Why imperative `readInto` instead of returning state:
 *   The waveform components already animate by writing bar heights to DOM refs in a
 *   rAF loop. Returning a new array on every animation frame would cause ~60 React
 *   re-renders per second. Instead the component calls `readInto` inside its own loop.
 *
 * Safety:
 *   - Microphone streams are NEVER connected to the destination (no echo/feedback).
 *   - Audio elements are connected source → analyser → destination so they remain
 *     audible. All app audio sources are same-origin (data: URLs / relative /media
 *     paths), so `createMediaElementSource` is not CORS-tainted.
 *   - If the shared AudioContext cannot be resumed (autoplay policy), the element is
 *     NOT tapped — playback is never broken; the visualiser simply falls back to flat.
 *   - Each element is tapped at most once (cached in a module-level WeakMap), because
 *     `createMediaElementSource` throws if called twice on the same element.
 *
 * @module useAudioAnalyser
 */

import { useCallback, useEffect, useRef } from 'react';
import { logger } from '@/lib/logger';

interface UseAudioAnalyserOptions {
  /** Microphone / recording stream to analyse (mutually exclusive with mediaElement). */
  stream?: MediaStream | null;
  /** Playing audio element to analyse (mutually exclusive with stream). */
  mediaElement?: HTMLAudioElement | null;
  /** When false, the analyser is torn down and readInto() returns false. */
  enabled: boolean;
}

export interface AudioAnalyserHandle {
  /**
   * Fill `out` with normalised 0–1 magnitudes sampled across the frequency
   * spectrum. Returns true if real data was written, false if no live analyser
   * is available (caller should render a flat/fallback state).
   */
  readInto: (out: number[]) => boolean;
}

/**
 * Pure helper: map a frequency-data array (each value 0–255) into `out.length`
 * normalised 0–1 bar heights arranged CENTER-OUT.
 *
 * The loudest part of the spectrum (low frequencies, where most voice/audio
 * energy lives) is placed in the MIDDLE bars and tapers symmetrically toward
 * both edges — producing a balanced, radiating "live audio" look instead of a
 * one-sided low→high slope. The result is mirror-symmetric: out[i] === out[n-1-i].
 *
 * Exported for unit testing without a Web Audio runtime.
 *
 * @returns the same `out` array, mutated in place.
 */
export function frequencyDataToCenteredBars(
  data: ArrayLike<number>,
  out: number[],
): number[] {
  const bins = data.length;
  const n = out.length;
  if (n === 0) return out;
  if (bins === 0) {
    for (let i = 0; i < n; i++) out[i] = 0;
    return out;
  }

  const center = (n - 1) / 2;
  const maxDist = center > 0 ? center : 1;

  for (let i = 0; i < n; i++) {
    // 0 at the centre bar → 1 at the outer edges
    const d = Math.abs(i - center) / maxDist;
    // centre samples the loudest low frequencies; edges sample the quiet highs
    const pos = d * (bins - 1);
    const lo = Math.floor(pos);
    const hi = Math.min(bins - 1, lo + 1);
    const frac = pos - lo;
    // Linear interpolation between adjacent bins keeps the taper smooth
    const value = data[lo] * (1 - frac) + data[hi] * frac;
    out[i] = value / 255;
  }
  return out;
}

// ---- Module-level shared resources -----------------------------------------

let sharedCtx: AudioContext | null = null;
// An audio element can only be tapped once; cache the source node per element.
const elementSources = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();
// Track which cached element sources have been wired to the destination.
const elementWiredToOutput = new WeakSet<HTMLMediaElement>();

let gestureListenersAttached = false;

function getSharedAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedCtx) {
    sharedCtx = new Ctor();
  }
  return sharedCtx;
}

/**
 * Create (if needed) and resume the shared AudioContext from within a user
 * gesture. A context auto-plays as `suspended` and `resume()` only succeeds
 * while a user activation is fresh — so we must do this inside a real gesture
 * handler, not from an async effect that runs after the click has ended.
 */
function ensureContextRunningFromGesture() {
  const ctx = getSharedAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

/**
 * Attach global gesture listeners that keep the shared AudioContext warm.
 *
 * Called when the hook MOUNTS (not when the context is first created), so the
 * listeners are already live during pre-audio screens (e.g. the speaking
 * mic-setup step). That way the user's "Continue"/navigation click creates and
 * resumes the context *before* the first question's audio auto-plays —
 * otherwise question 1 would analyse a still-suspended context and show flat
 * bars, only recovering on a later click.
 */
function attachGestureResume() {
  if (gestureListenersAttached || typeof window === 'undefined') return;
  gestureListenersAttached = true;
  // Not `once` — every interaction re-warms the context if it lapsed.
  window.addEventListener('pointerdown', ensureContextRunningFromGesture, { capture: true });
  window.addEventListener('keydown', ensureContextRunningFromGesture, { capture: true });
  window.addEventListener('touchstart', ensureContextRunningFromGesture, { capture: true, passive: true });
}

// ---- Hook ------------------------------------------------------------------

export function useAudioAnalyser({
  stream,
  mediaElement,
  enabled,
}: UseAudioAnalyserOptions): AudioAnalyserHandle {
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  // For stream sources we create a fresh node each time and must disconnect it.
  const streamSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  // For element sources we keep the (cached) node so we can disconnect just our analyser.
  const elementSourceRef = useRef<MediaElementAudioSourceNode | null>(null);

  // Start listening for user gestures as soon as the hook mounts (e.g. during
  // the mic-setup screen) so the shared AudioContext is warmed by the click
  // that precedes the first question's audio.
  useEffect(() => {
    attachGestureResume();
  }, []);

  useEffect(() => {
    const source = mediaElement ?? stream ?? null;
    if (!enabled || !source) {
      return undefined;
    }

    const ctx = getSharedAudioContext();
    if (!ctx) {
      return undefined; // Web Audio unsupported — graceful fallback to flat
    }

    let cancelled = false;

    async function setup() {
      // Resume the context (autoplay policy may start it suspended).
      if (ctx!.state === 'suspended') {
        try {
          await ctx!.resume();
        } catch (e) {
          logger.warn('useAudioAnalyser: AudioContext.resume() failed', e);
        }
      }
      if (cancelled) return;

      const analyser = ctx!.createAnalyser();
      analyser.fftSize = 64; // 32 frequency bins — plenty for a bar visualiser
      analyser.smoothingTimeConstant = 0.7;

      try {
        if (mediaElement) {
          // Only tap an element when the context is actually running, otherwise
          // routing through Web Audio would silence playback.
          if (ctx!.state !== 'running') {
            logger.warn('useAudioAnalyser: context not running; skipping element tap');
            return;
          }
          let src = elementSources.get(mediaElement);
          if (!src) {
            src = ctx!.createMediaElementSource(mediaElement);
            elementSources.set(mediaElement, src);
          }
          if (!elementWiredToOutput.has(mediaElement)) {
            src.connect(ctx!.destination); // keep audio audible (once per element)
            elementWiredToOutput.add(mediaElement);
          }
          src.connect(analyser);
          elementSourceRef.current = src;
        } else if (stream) {
          const src = ctx!.createMediaStreamSource(stream);
          src.connect(analyser); // NOT connected to destination — avoids echo
          streamSourceRef.current = src;
        }
      } catch (e) {
        logger.warn('useAudioAnalyser: failed to tap audio source', e);
        return;
      }

      if (cancelled) return;
      analyserRef.current = analyser;
      dataRef.current = new Uint8Array(analyser.frequencyBinCount);
    }

    setup();

    return () => {
      cancelled = true;
      if (analyserRef.current && elementSourceRef.current) {
        // Disconnect only our analyser; keep the element→destination link alive.
        try {
          elementSourceRef.current.disconnect(analyserRef.current);
        } catch {
          /* already disconnected */
        }
      }
      if (streamSourceRef.current) {
        try {
          streamSourceRef.current.disconnect();
        } catch {
          /* noop */
        }
        streamSourceRef.current = null;
      }
      if (analyserRef.current) {
        try {
          analyserRef.current.disconnect();
        } catch {
          /* noop */
        }
      }
      analyserRef.current = null;
      dataRef.current = null;
      elementSourceRef.current = null;
    };
  }, [enabled, stream, mediaElement]);

  const readInto = useCallback((out: number[]): boolean => {
    const analyser = analyserRef.current;
    const data = dataRef.current;
    if (!analyser || !data) return false;

    analyser.getByteFrequencyData(data);
    frequencyDataToCenteredBars(data, out);
    return true;
  }, []);

  return { readInto };
}
